/**
 * Pure helpers behind the status-bar indicator and the Stream Health dock:
 * which issue speaks for the whole stream, and how each measurement reads.
 */
import type { HealthEvent, HealthIssue, HealthState } from '../../health/api';

export type HealthTone = 'idle' | 'healthy' | 'warning' | 'critical';

const SEVERITY_RANK: Record<HealthIssue['severity'], number> = { info: 0, warning: 1, critical: 2 };

/** Most severe first; among equals the one that has lasted longest. */
export function sortIssues(issues: readonly HealthIssue[]): HealthIssue[] {
  return [...issues].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || a.since - b.since);
}

export interface HealthSummary {
  tone: HealthTone;
  label: string;
  /** Message of the issue that decides the tone; null when there is nothing to say. */
  detail: string | null;
  /** Issues of warning severity or worse. */
  count: number;
}

const LABELS: Record<HealthTone, string> = { idle: 'Not live', healthy: 'Healthy', warning: 'Warning', critical: 'Critical' };

/**
 * The tone never reads calmer than the worst active issue, whatever `status`
 * says: a critical issue in the list is shown as critical even if the two
 * fields were momentarily out of step.
 */
export function summarizeHealth(health: HealthState): HealthSummary {
  const ordered = sortIssues(health.issues).filter((issue) => issue.severity !== 'info');
  const worst = ordered[0];
  let tone: HealthTone = health.status;
  if (worst?.severity === 'critical') tone = 'critical';
  else if (worst?.severity === 'warning' && tone !== 'critical') tone = 'warning';
  return { tone, label: LABELS[tone], detail: worst?.message ?? null, count: ordered.length };
}

export const RECOVERY_LABELS: Record<HealthIssue['recovery'], string> = {
  none: 'No automatic recovery',
  attempting: 'Recovering…',
  recovered: 'Recovered',
  failed: 'Recovery failed',
};

export const EVENT_LABELS: Record<HealthEvent['type'], string> = {
  'issue-raised': 'Raised',
  'issue-cleared': 'Cleared',
  'recovery-attempted': 'Recovery started',
  'recovery-succeeded': 'Recovered',
  'recovery-failed': 'Recovery failed',
};

const DASH = '—';

export function formatFps(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value) ? DASH : value.toFixed(1);
}

/** A duration for "how stale is this": 850 ms, 3.2 s, 4 min 05 s. */
export function formatAge(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return DASH;
  const value = Math.max(0, ms);
  if (value < 1000) return `${Math.round(value)} ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(1)} s`;
  const minutes = Math.floor(value / 60_000);
  const seconds = Math.floor((value % 60_000) / 1000);
  return `${minutes} min ${String(seconds).padStart(2, '0')} s`;
}

/** Signed milliseconds for skew and lip-sync figures. */
export function formatSkew(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return DASH;
  const rounded = Math.round(ms);
  return `${rounded > 0 ? '+' : ''}${rounded} ms`;
}

export function formatPercent(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value) ? DASH : `${value.toFixed(1)}%`;
}

export function formatFlag(value: boolean | null | undefined, yes: string, no: string): string {
  return value === null || value === undefined ? DASH : value ? yes : no;
}

/** "VideoToolbox (hardware)" / "libvpx (software)" / "libvpx" when the browser does not say. */
export function formatEncoder(implementation: string | null | undefined, powerEfficient: boolean | null | undefined): string {
  if (!implementation) return DASH;
  if (powerEfficient === null || powerEfficient === undefined) return implementation;
  return `${implementation} (${powerEfficient ? 'hardware' : 'software'})`;
}

export function formatClock(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** True when a 'relay' policy has no TURN server to relay through. */
export function relayWithoutTurn(policy: 'all' | 'relay', servers: readonly { urls: string | string[] }[]): boolean {
  if (policy !== 'relay') return false;
  return !servers.some((server) => (Array.isArray(server.urls) ? server.urls : [server.urls]).some((url) => /^turns?:/i.test(url)));
}
